/**
 * useDetailDrawer Hook
 *
 * Manages detail drawer state for viewing record details.
 */

import { useState, useCallback } from 'react'
import type { UseDetailDrawerResult } from '../types'

/**
 * Hook to manage detail drawer state.
 *
 * @returns Drawer state and actions
 */
export function useDetailDrawer(): UseDetailDrawerResult {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<Record<string, unknown> | null>(null)

  const openDrawer = useCallback((record: Record<string, unknown>) => {
    setSelectedRecord(record)
    setIsOpen(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setIsOpen(false)
    // Delay clearing record to allow close animation
    setTimeout(() => {
      setSelectedRecord(null)
    }, 300)
  }, [])

  return {
    isOpen,
    selectedRecord,
    openDrawer,
    closeDrawer,
  }
}

/**
 * Hook to manage drawer with keyboard navigation.
 * Extends useDetailDrawer with Previous/Next record navigation.
 *
 * @param data - Array of records for navigation
 * @param rowKey - Key field to identify records (default: 'uid')
 * @returns Drawer state with navigation
 */
export function useDetailDrawerWithNavigation(
  data: Record<string, unknown>[],
  rowKey: string = 'uid'
) {
  const { isOpen, selectedRecord, openDrawer, closeDrawer } = useDetailDrawer()

  // Find current index
  const currentIndex = selectedRecord
    ? data.findIndex((r) => r[rowKey] === selectedRecord[rowKey])
    : -1

  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex >= 0 && currentIndex < data.length - 1

  const goToPrevious = useCallback(() => {
    if (hasPrevious) {
      openDrawer(data[currentIndex - 1])
    }
  }, [hasPrevious, currentIndex, data, openDrawer])

  const goToNext = useCallback(() => {
    if (hasNext) {
      openDrawer(data[currentIndex + 1])
    }
  }, [hasNext, currentIndex, data, openDrawer])

  return {
    isOpen,
    selectedRecord,
    openDrawer,
    closeDrawer,
    hasPrevious,
    hasNext,
    goToPrevious,
    goToNext,
    currentIndex: currentIndex + 1, // 1-indexed for display
    totalRecords: data.length,
  }
}
